// Modal de compra de créditos SMS — mockup "Modal Créditos SMS": pacotes em
// cards com selo de melhor preço, consumo médio no subtítulo e o Pix gerado
// dentro do modal.
//
// O Pix vem do Mercado Pago (`POST /sms/recargas` devolve `pix` com o
// copia-e-cola e o QR em base64). Se a conta não tiver Pix habilitado, o
// backend devolve `pix: null` e o modal cai no link do Checkout Pro.
//
// A "quantidade personalizada" do mockup continua fora: o backend só aceita
// `smsPackageId` e faz snapshot da quantidade/valor do pacote.
import React, { useContext, useEffect, useRef, useState } from "react";

import { makeStyles, alpha } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";

import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import SyncIcon from "@material-ui/icons/Sync";
import QrCodeIcon from "@mui/icons-material/QrCode2";
import { CopyToClipboard } from "react-copy-to-clipboard";
import QRCode from "react-qr-code";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import AerosDialog from "../AerosDialog";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { AEROS, stateText } from "../../styles/aerosTokens";
import {
  bestValuePackId,
  purchaseSummary,
  unitPrice,
  formatBRL,
  formatUnit,
} from "../../pages/CreditosSms/smsRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight
    ? theme.palette.aeros.borderStrong
    : "rgba(255,255,255,0.1)";
  return {
    // 2 colunas com o nome do pacote, como no mockup "Modal Comprar
    // Créditos" — em 4 colunas o nome não cabe e o card vira só um número.
    packGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0,1fr))",
      gap: 10,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    pack: {
      border: `1px solid ${borderInput}`,
      backgroundColor: theme.palette.aeros.surface,
      borderRadius: 12,
      padding: 14,
      position: "relative",
      textAlign: "left",
      cursor: "pointer",
      font: "inherit",
      color: theme.palette.text.primary,
      display: "flex",
      flexDirection: "column",
      gap: 2,
      minWidth: 0,
      "&:hover": { borderColor: "rgba(160,68,255,0.4)" },
    },
    packName: {
      fontSize: 13,
      fontWeight: 700,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      paddingRight: 24,
    },
    packCheck: {
      position: "absolute",
      top: 12,
      right: 12,
      fontSize: 18,
      color: stateText(theme, AEROS.cyan, "info"),
    },
    packOn: {
      borderColor: "rgba(160,68,255,0.6)",
      backgroundColor: alpha("#A044FF", 0.1),
    },
    packTag: {
      position: "absolute",
      top: -9,
      left: "50%",
      transform: "translateX(-50%)",
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "#07080D",
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      borderRadius: 99,
      padding: "2px 8px",
      whiteSpace: "nowrap",
    },
    packQty: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 20,
      fontWeight: 800,
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
    },
    packUnitLabel: { fontSize: 11, color: theme.palette.text.secondary },
    packPrice: {
      fontSize: 14,
      fontWeight: 700,
      marginTop: 8,
      fontVariantNumeric: "tabular-nums",
    },
    packUnit: {
      fontSize: 10.5,
      color: theme.palette.aeros.textTertiary,
      marginTop: 2,
    },
    summary: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      padding: "0 4px",
    },
    row: { display: "flex" },
    rowValue: {
      marginLeft: "auto",
      color: theme.palette.text.primary,
      fontVariantNumeric: "tabular-nums",
    },
    rowValueCyan: {
      marginLeft: "auto",
      color: stateText(theme, AEROS.cyan, "info"),
      fontVariantNumeric: "tabular-nums",
    },
    totalRow: {
      display: "flex",
      paddingTop: 6,
      borderTop: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      fontWeight: 700,
      color: theme.palette.text.primary,
      fontSize: 14,
    },
    waiting: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      fontSize: 12,
      color: theme.palette.text.secondary,
      backgroundColor: alpha(AEROS.cyan, 0.06),
      border: `1px solid ${alpha(AEROS.cyan, 0.2)}`,
      borderRadius: 10,
      padding: "9px 12px",
    },
    empty: {
      border: `1px dashed ${borderInput}`,
      borderRadius: 12,
      padding: 20,
      textAlign: "center",
      fontSize: 12.5,
      color: theme.palette.aeros.textTertiary,
    },
    footNote: {
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      display: "flex",
      alignItems: "center",
      gap: 5,
      "& .MuiSvgIcon-root": { fontSize: 14 },
    },
    balance: { color: stateText(theme, AEROS.cyan, "info"), fontWeight: 700 },

    // ---- painel do Pix, quando gerado ----
    pixPane: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 12,
      backgroundColor: isLight ? theme.palette.aeros.surface2 : "#0E1119",
      border: `1px solid ${border}`,
      borderRadius: 14,
      padding: 18,
    },
    qrBox: {
      width: 180,
      height: 180,
      borderRadius: 14,
      backgroundColor: "#F1F2F6",
      padding: 10,
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& img": { width: "100%", height: "100%", objectFit: "contain" },
    },
    codeRow: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: 8,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      padding: "8px 12px",
      boxSizing: "border-box",
    },
    code: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontFamily: "ui-monospace, monospace",
      fontSize: 11.5,
      color: theme.palette.text.secondary,
    },
    copyButton: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      fontWeight: 600,
      fontSize: 12,
      color: stateText(theme, AEROS.cyan, "info"),
      background: "none",
      border: "none",
      cursor: "pointer",
      fontFamily: "inherit",
      flexShrink: 0,
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
    copied: { color: stateText(theme, AEROS.success, "success") },
    checkoutLink: {
      fontSize: 11.5,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      background: "none",
      border: "none",
      cursor: "pointer",
      fontFamily: "inherit",
      textDecoration: "underline",
      "&:hover": { color: theme.palette.text.primary },
    },
  };
});

const ComprarCreditosSmsModal = ({ open, onClose, onSaldoAtualizado }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [saldo, setSaldo] = useState(null);
  const [pacotes, setPacotes] = useState([]);
  const [pacoteSelecionado, setPacoteSelecionado] = useState(null);
  const [criando, setCriando] = useState(false);
  const [aguardando, setAguardando] = useState(false);
  const [consumoMedio, setConsumoMedio] = useState(null);
  const [pix, setPix] = useState(null);
  const [checkoutLink, setCheckoutLink] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const orderIdRef = useRef(null);

  const companyId = user.companyId;

  useEffect(() => {
    if (!open) return;
    api
      .get("/sms/saldo")
      .then(({ data }) => setSaldo(data?.saldo ?? null))
      .catch(() => setSaldo(null));
    api
      .get("/sms/pacotes")
      .then(({ data }) => setPacotes(data || []))
      .catch(() => setPacotes([]));
    // Silencioso: sem histórico o consumo some do subtítulo, não vira erro.
    api
      .get("/sms/consumo-medio")
      .then(({ data }) => setConsumoMedio(data?.consumoMedio ?? null))
      .catch(() => setConsumoMedio(null));
    setPacoteSelecionado(null);
    setAguardando(false);
    setPix(null);
    setCheckoutLink(null);
    setCopiado(false);
    orderIdRef.current = null;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onSmsRecargaEvent = (data) => {
      if (data.action !== "paid") return;
      setSaldo(data.saldo);
      setAguardando(false);
      toast.success(i18n.t("smsCredits.paid"));
      if (onSaldoAtualizado) onSaldoAtualizado(data.saldo);
      if (orderIdRef.current && data.orderId === orderIdRef.current) {
        onClose();
      }
    };

    socket.on(`company-${companyId}-sms-recarga`, onSmsRecargaEvent);
    return () => {
      socket.off(`company-${companyId}-sms-recarga`, onSmsRecargaEvent);
    };
  }, [open, socket, companyId, onClose, onSaldoAtualizado]);

  const handleClose = () => {
    if (criando) return;
    onClose();
  };

  const handleComprar = async () => {
    if (!pacoteSelecionado) return;
    setCriando(true);
    try {
      const { data } = await api.post("/sms/recargas", {
        smsPackageId: pacoteSelecionado.id,
      });
      orderIdRef.current = data.order?.id ?? null;
      setCheckoutLink(data.link || null);
      if (data.pix?.qrCode) {
        // Pix gerado: o QR fica no próprio modal, sem abrir aba.
        setPix(data.pix);
      } else if (data.link) {
        window.open(data.link, "_blank");
      }
      setAguardando(true);
    } catch (err) {
      toastError(err);
    } finally {
      setCriando(false);
    }
  };

  const bestId = bestValuePackId(pacotes);
  const summary = purchaseSummary(pacoteSelecionado, saldo);

  return (
    <AerosDialog
      open={open}
      onClose={handleClose}
      width={680}
      title={i18n.t("smsCredits.title")}
      subtitle={
        saldo === null
          ? i18n.t("smsCredits.subtitleUnknown")
          : `${i18n.t("smsCredits.subtitle", { balance: saldo })}${
              // Sem histórico o consumo médio some, em vez de dizer "0/mês".
              consumoMedio ? ` · ${i18n.t("smsCredits.avgUsage", { avg: consumoMedio })}` : ""
            }`
      }
      onSubmit={handleComprar}
      loading={criando}
      submitDisabled={!pacoteSelecionado}
      submitIcon={<QrCodeIcon />}
      submitLabel={
        summary
          ? `${i18n.t("smsCredits.generatePix")} · ${formatBRL(summary.total)}`
          : i18n.t("smsCredits.generatePix")
      }
      cancelLabel={i18n.t("aeros.confirm.cancel")}
      footerLeft={
        <span className={classes.footNote}>
          <InfoOutlinedIcon />
          {i18n.t("smsCredits.footNote")}
        </span>
      }
    >
      {pix && (
        <div className={classes.pixPane}>
          <div className={classes.qrBox}>
            {pix.qrCodeBase64 ? (
              <img src={`data:image/png;base64,${pix.qrCodeBase64}`} alt="" />
            ) : (
              <QRCode value={pix.qrCode} size={160} />
            )}
          </div>

          <div className={classes.codeRow}>
            <span className={classes.code} title={pix.qrCode}>
              {pix.qrCode}
            </span>
            <CopyToClipboard
              text={pix.qrCode}
              onCopy={() => {
                setCopiado(true);
                toast.success(i18n.t("smsCredits.codeCopied"));
                setTimeout(() => setCopiado(false), 1500);
              }}
            >
              <button
                type="button"
                className={`${classes.copyButton} ${copiado ? classes.copied : ""}`}
              >
                {copiado ? <CheckCircleOutlineIcon /> : <FileCopyOutlinedIcon />}
                {i18n.t("smsCredits.copyCode")}
              </button>
            </CopyToClipboard>
          </div>

          <div className={classes.waiting} style={{ width: "100%", boxSizing: "border-box" }}>
            <SyncIcon style={{ fontSize: 16 }} />
            {i18n.t("smsCredits.waitingPix")}
          </div>

          {checkoutLink && (
            <button
              type="button"
              className={classes.checkoutLink}
              onClick={() => window.open(checkoutLink, "_blank")}
            >
              {i18n.t("smsCredits.otherMethods")}
            </button>
          )}
        </div>
      )}

      {!pix && aguardando && (
        <div className={classes.waiting}>
          <CircularProgress size={14} />
          {i18n.t("smsCredits.waiting")}
        </div>
      )}

      {pix ? null : pacotes.length === 0 ? (
        <div className={classes.empty}>{i18n.t("smsCredits.empty")}</div>
      ) : (
        <div className={classes.packGrid}>
          {pacotes.map((pacote) => {
            const on = pacoteSelecionado?.id === pacote.id;
            const unit = formatUnit(unitPrice(pacote));
            return (
              <button
                key={pacote.id}
                type="button"
                aria-pressed={on}
                className={`${classes.pack} ${on ? classes.packOn : ""}`}
                onClick={() => setPacoteSelecionado(pacote)}
              >
                {pacote.id === bestId && (
                  <span className={classes.packTag}>
                    {i18n.t("smsCredits.bestValue")}
                  </span>
                )}
                {on && <CheckCircleIcon className={classes.packCheck} />}
                <div className={classes.packName} title={pacote.nome}>
                  {pacote.nome}
                </div>
                <div className={classes.packUnitLabel}>
                  {Number(pacote.quantidade).toLocaleString("pt-BR")}{" "}
                  {i18n.t("smsCredits.sms")}
                </div>
                <div className={classes.packPrice}>{formatBRL(pacote.valor)}</div>
                {unit && (
                  <div className={classes.packUnit}>
                    {i18n.t("smsCredits.perSms", { price: unit })}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {!pix && summary && (
        <div className={classes.summary}>
          <div className={classes.row}>
            {/* "2.000 SMS × R$ 0,085" — mostrar a conta deixa claro de onde
                sai o total, como no mockup. */}
            <span>
              {summary.quantity.toLocaleString("pt-BR")} {i18n.t("smsCredits.sms")}
              {summary.unit !== null && ` × ${formatUnit(summary.unit)}`}
            </span>
            <span className={classes.rowValue}>{formatBRL(summary.total)}</span>
          </div>
          {summary.newBalance !== null && (
            <div className={classes.row}>
              <span>{i18n.t("smsCredits.newBalance")}</span>
              <span className={classes.rowValueCyan}>
                {summary.newBalance.toLocaleString("pt-BR")}{" "}
                {i18n.t("smsCredits.sms")}
              </span>
            </div>
          )}
          <div className={classes.totalRow}>
            <span>{i18n.t("smsCredits.total")}</span>
            <span className={classes.rowValue}>{formatBRL(summary.total)}</span>
          </div>
        </div>
      )}
    </AerosDialog>
  );
};

export default ComprarCreditosSmsModal;
